class AveyCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    window.AveyBottleExperiment?.prepareCart(this);
    this.sectionId = this.dataset.sectionId;
    this.dialog = this.querySelector('[data-avey-overlay]');

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeydown);
    this.dialog?.addEventListener('avey:overlay:request-close', this.onOverlayRequestClose);
    document.addEventListener('avey:cart:added', this.onCartAdded);
    this.removeUnqualifiedDiscoveryDiscount();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('avey:overlay:request-close', this.onOverlayRequestClose);
    document.removeEventListener('avey:cart:added', this.onCartAdded);
  }

  onClick = (event) => {
    const shadeClose = event.target.closest('[data-cart-shade-close]');
    if (shadeClose) {
      this.closeShadePicker();
      return;
    }

    const shadeOption = event.target.closest('[data-cart-shade-option]');
    if (shadeOption) {
      this.changeLineVariant(shadeOption);
      return;
    }

    const shadeTrigger = event.target.closest('[data-cart-shade-open]');
    if (shadeTrigger) {
      this.openShadePicker(shadeTrigger);
      return;
    }

    const recommendationButton = event.target.closest('[data-cart-recommendation-add]');
    if (recommendationButton) {
      this.addRecommendation(recommendationButton);
      return;
    }

    const line = event.target.closest('[data-cart-line]');
    if (!line) return;

    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    if (quantityButton) {
      const input = line.querySelector('[data-cart-quantity]');
      const nextQuantity = this.normalizeQuantity(
        Number(input?.value || 0) + Number(quantityButton.dataset.cartQuantityChange)
      );
      this.changeLine(line.dataset.lineKey, nextQuantity, line, null, quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      this.changeLine(line.dataset.lineKey, 0, line, removeButton);
    }
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && !this.shadePicker?.hidden) {
      event.preventDefault();
      event.stopPropagation();
      window.AveyOverlayManager?.requestClose(this.dialog, { reason: 'escape' });
    }
  };

  onOverlayRequestClose = (event) => {
    if (event.detail?.overlay !== this.dialog || !this.shadePicker || this.shadePicker.hidden) return;
    event.preventDefault();
    this.closeShadePicker();
  };

  onChange = (event) => {
    const pageShade = event.target.closest('[data-cart-page-variant]');
    if (pageShade) {
      this.activeShadeLine = pageShade.closest('[data-cart-line]');
      this.changeLineVariant(pageShade.selectedOptions[0]);
      return;
    }

    const recommendationSelect = event.target.closest('[data-cart-recommendation-variant]');
    if (recommendationSelect) {
      const card = recommendationSelect.closest('[data-cart-recommendation]');
      const button = card?.querySelector('[data-cart-recommendation-add]');
      const price = card?.querySelector('[data-cart-recommendation-price]');
      const option = recommendationSelect.selectedOptions[0];
      if (button) button.dataset.variantId = recommendationSelect.value;
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      this.updateShadeSelectVisual(recommendationSelect);
      return;
    }

    const input = event.target.closest('[data-cart-quantity]');
    const line = input?.closest('[data-cart-line]');
    if (!input || !line) return;

    this.changeLine(line.dataset.lineKey, this.normalizeQuantity(input.value), line);
  };

  updateShadeSelectVisual(select) {
    const visual = select.closest('[data-shade-select]')?.querySelector('[data-shade-select-visual]');
    const option = select.selectedOptions[0];
    if (!visual || !option) return;

    const image = option.dataset.shadeImage || '';
    visual.classList.toggle('avey-shade-select__visual--fallback', !image);
    visual.style.setProperty('--avey-shade-image', image ? `url("${image}")` : 'none');
  }

  onCartAdded = async (event) => {
    const sectionHTML = event.detail?.sections?.[this.sectionId];
    try {
      if (sectionHTML) this.renderSection(sectionHTML);
      else await this.refreshSection();
    } catch (error) {
      // The add already succeeded. Show Shopify's cart without repeating it.
      window.location.assign(`${window.Shopify.routes.root}cart`);
      return;
    }

    if (this.dialog) window.AveyOverlayManager?.open(this.dialog, event.detail?.opener);
    await this.removeUnqualifiedDiscoveryDiscount();
  };

  async changeLine(key, quantity, line = null, removeButton = null, quantityButton = null) {
    if (!key || this.dataset.loading === 'true') return;
    this.setLoading(true);
    this.setLineQuantityLoading(line, !removeButton, quantityButton);
    this.setRemoveButtonLoading(removeButton, true);
    this.setError('');
    let confirmedCart = null;
    let experimentProperties = null;
    if (line && quantity > 0 && Object.keys(window.AveyBottleExperiment?.properties() || {}).length) {
      try {
        const previous = this.normalizeProperties(JSON.parse(line.querySelector('[data-cart-line-properties]')?.textContent || '{}'));
        experimentProperties = window.AveyBottleExperiment.properties(previous);
      } catch (_) { /* Keep the native quantity action available if legacy properties cannot be read. */ }
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity,
          ...(experimentProperties ? { properties: experimentProperties } : {}),
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || this.dataset.errorLabel);

      confirmedCart = cart;
      const sectionHTML = cart.sections?.[this.sectionId];
      if (sectionHTML) {
        const rendered = quantity === 0
          ? await this.renderLineRemoval(sectionHTML, line)
          : this.renderStableSection(sectionHTML, { line });
        if (!rendered) this.renderSection(sectionHTML);
      } else {
        await this.refreshSection();
      }

      this.updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('avey:cart:updated', {
        detail: { cart, source: 'cart-drawer' },
      }));
      await this.removeUnqualifiedDiscoveryDiscount();
    } catch (error) {
      // A confirmed quantity change must never be repeated when only its section render failed.
      if (confirmedCart) {
        try {
          await this.refreshSection();
          this.updateCartCount(confirmedCart.item_count);
          document.dispatchEvent(new CustomEvent('avey:cart:updated', { detail: { cart: confirmedCart, source: 'cart-drawer' } }));
          return;
        } catch (_) { /* Preserve the native error when the fresh read also fails. */ }
      }
      this.setError(error.message || this.dataset.errorLabel);
    } finally {
      this.setLineQuantityLoading(line, false, quantityButton);
      this.setRemoveButtonLoading(removeButton, false);
      this.setLoading(false);
    }
  }

  openShadePicker(trigger) {
    const line = trigger?.closest('[data-cart-line]');
    const template = line?.querySelector('[data-cart-shade-options]');
    const picker = this.shadePicker;
    const content = picker?.querySelector('[data-cart-shade-picker-content]');
    if (!line || !(template instanceof HTMLTemplateElement) || !picker || !content) return;

    if (this.shadeCloseTimer) window.clearTimeout(this.shadeCloseTimer);
    this.activeShadeLine = line;
    this.shadeReturnFocus = trigger;
    content.replaceChildren(template.content.cloneNode(true));
    picker.hidden = false;
    picker.setAttribute('aria-hidden', 'false');
    window.AveyOverlayManager?.observeHeaders(picker.closest('[data-avey-overlay]') || picker);
    requestAnimationFrame(() => {
      picker.classList.add('is-open');
    });
  }

  closeShadePicker({ restoreFocus = true } = {}) {
    const picker = this.shadePicker;
    if (!picker || picker.hidden || picker.getAttribute('aria-hidden') === 'true') return;
    picker.classList.remove('is-open');
    picker.setAttribute('aria-hidden', 'true');
    if (restoreFocus) this.shadeReturnFocus?.focus({ preventScroll: true });
    this.shadeCloseTimer = window.setTimeout(() => {
      picker.hidden = true;
      picker.querySelector('[data-cart-shade-picker-content]')?.replaceChildren();
      this.activeShadeLine = null;
    }, 220);
  }

  async changeLineVariant(option) {
    const line = this.activeShadeLine;
    const selectedVariantId = Number(option?.dataset.variantId);
    const currentVariantId = Number(line?.dataset.currentVariantId);
    const quantity = this.normalizeQuantity(line?.dataset.lineQuantity || 1);
    if (!line || !selectedVariantId || !quantity || this.dataset.loading === 'true') return;
    if (selectedVariantId === currentVariantId) {
      this.closeShadePicker();
      return;
    }

    const propertiesScript = line.querySelector('[data-cart-line-properties]');
    let properties = {};
    try {
      properties = JSON.parse(propertiesScript?.textContent || '{}');
      if (Array.isArray(properties)) {
        properties = Object.fromEntries(
          properties.filter((property) => Array.isArray(property) && property.length >= 2)
        );
      } else if (!properties || typeof properties !== 'object') {
        properties = {};
      }
    } catch (_error) {
      properties = {};
    }

    const item = { id: selectedVariantId, quantity, properties };
    const sellingPlanId = Number(line.dataset.sellingPlanId);
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    let selectedVariantPreviousQuantity = 0;
    let variantWasAdded = false;
    let originalWasRemoved = false;

    // Optimistic visual confirmation: move the selected outline as soon as
    // the customer taps, while the loader handles the cart request.
    const shadeOptions = [...(option.closest('.avey-cart-shade-picker__grid')
      ?.querySelectorAll('[data-cart-shade-option]') || [])];
    shadeOptions.forEach((shadeOption) => {
      shadeOption.setAttribute('aria-pressed', String(shadeOption === option));
    });

    this.setLoading(true);
    this.setShadeOptionLoading(option, true);
    this.setError('');

    try {
      const cartBeforeChange = await this.fetchCart();
      const selectedLineBeforeChange = this.findCartLine(cartBeforeChange, {
        variantId: selectedVariantId,
        properties,
        sellingPlanId,
      });
      selectedVariantPreviousQuantity = selectedLineBeforeChange?.quantity || 0;

      // Remove the exact source line before adding its replacement. Automatic
      // discounts may split identical variants into several lines and change
      // their keys after an addition; removing first keeps the intended line
      // unambiguous and prevents a sibling shade from being deleted.
      const removeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: line.dataset.lineKey, quantity: 0 }),
      });
      const removed = await removeResponse.json();
      if (!removeResponse.ok) throw new Error(removed.description || removed.message || this.dataset.shadeErrorLabel);
      originalWasRemoved = true;

      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });
      const added = await addResponse.json();
      if (!addResponse.ok) throw new Error(added.description || added.message || this.dataset.shadeErrorLabel);
      variantWasAdded = true;

      await this.refreshSection({ line, selectedVariantId });
      await new Promise((resolve) => requestAnimationFrame(resolve));
      this.closeShadePicker({ restoreFocus: false });

      const cart = await this.fetchCart();
      this.updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('avey:cart:updated', {
        detail: { cart, source: 'cart-shade' },
      }));
    } catch (error) {
      const message = error.message || this.dataset.shadeErrorLabel || this.dataset.errorLabel;
      if (variantWasAdded) {
        await this.restoreVariantQuantity({
          variantId: selectedVariantId,
          properties,
          sellingPlanId,
          quantity: selectedVariantPreviousQuantity,
        }).catch(() => {});
      }
      if (originalWasRemoved) {
        const originalItem = { id: currentVariantId, quantity, properties };
        if (sellingPlanId) originalItem.selling_plan = sellingPlanId;
        await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [originalItem] }),
        }).catch(() => {});
      }
      this.closeShadePicker({ restoreFocus: false });
      await this.refreshSection().catch(() => {});
      this.setError(message);
    } finally {
      this.setShadeOptionLoading(option, false);
      this.setLoading(false);
    }
  }

  async fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || this.dataset.errorLabel);
    return cart;
  }

  async removeUnqualifiedDiscoveryDiscount() {
    const state = this.querySelector('[data-cart-discount-state]');
    if (
      !state
      || state.dataset.discoveryOfferQualified === 'true'
      || state.dataset.discoveryOfferDiscountApplied !== 'true'
      || this.discoveryDiscountCleanupPending
    ) return false;

    this.discoveryDiscountCleanupPending = true;
    this.setLoading(true);
    try {
      const retainedCodes = [...state.querySelectorAll('[data-cart-retained-discount-code]')]
        .map((element) => element.dataset.cartRetainedDiscountCode)
        .filter(Boolean);
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: [...new Set(retainedCodes)].join(','),
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || this.dataset.errorLabel);

      if (window.location.pathname.replace(/\/+$/, '').endsWith('/cart')) {
        window.location.reload();
        return true;
      }

      const sectionHTML = cart.sections?.[this.sectionId];
      if (sectionHTML) this.renderSection(sectionHTML);
      else await this.refreshSection();
      this.updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('avey:cart:updated', {
        detail: { cart, source: 'discovery-discount-cleanup' },
      }));
      return true;
    } catch (error) {
      this.setError(error.message || this.dataset.errorLabel);
      return false;
    } finally {
      this.discoveryDiscountCleanupPending = false;
      this.setLoading(false);
    }
  }

  normalizeProperties(properties) {
    if (Array.isArray(properties)) {
      return Object.fromEntries(
        properties.filter((property) => Array.isArray(property) && property.length >= 2)
      );
    }
    return properties && typeof properties === 'object' ? properties : {};
  }

  propertiesMatch(left, right) {
    const normalizedLeft = this.normalizeProperties(left);
    const normalizedRight = this.normalizeProperties(right);
    const leftKeys = Object.keys(normalizedLeft).sort();
    const rightKeys = Object.keys(normalizedRight).sort();
    return leftKeys.length === rightKeys.length
      && leftKeys.every((key, index) => (
        key === rightKeys[index]
        && String(normalizedLeft[key]) === String(normalizedRight[key])
      ));
  }

  findCartLine(cart, { variantId, properties, sellingPlanId = 0, preferredKey = '' }) {
    const candidates = (cart?.items || []).filter((cartItem) => {
      const cartSellingPlanId = Number(cartItem.selling_plan_allocation?.selling_plan?.id || 0);
      return Number(cartItem.variant_id) === Number(variantId)
        && cartSellingPlanId === Number(sellingPlanId || 0)
        && this.propertiesMatch(cartItem.properties, properties);
    });
    return candidates.find((cartItem) => cartItem.key === preferredKey) || candidates[0] || null;
  }

  async restoreVariantQuantity({ variantId, properties, sellingPlanId, quantity }) {
    const cart = await this.fetchCart();
    const replacementLine = this.findCartLine(cart, { variantId, properties, sellingPlanId });
    if (!replacementLine?.key) return;

    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: replacementLine.key, quantity }),
    });
  }

  async addRecommendation(button) {
    if (!button || this.dataset.loading === 'true') return;
    const variantId = Number(button.dataset.variantId);
    const recommendation = button.closest('[data-cart-recommendation]');
    const addSource = recommendation?.dataset.recommendationContext === 'empty'
      ? 'empty_cart_recommendation'
      : this.hasAttribute('data-cart-page') ? 'cart_page_recommendation' : 'cart_drawer_upsell';
    const properties = { _avey_add_source: addSource, ...(window.AveyBottleExperiment?.properties() || {}) };
    if (!variantId) return;

    this.setLoading(true);
    this.setError('');
    this.setRecommendationButtonState(button, 'loading');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties,
          }],
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.dataset.errorLabel);

      this.setRecommendationButtonState(button, 'success');
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      await new Promise((resolve) => window.setTimeout(resolve, 320));

      const sectionHTML = result.sections?.[this.sectionId];
      if (sectionHTML) {
        if (!(await this.renderRecommendationAdd(sectionHTML, button))) this.renderSection(sectionHTML);
      } else {
        await this.refreshSection();
      }

      if (cart) this.updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('avey:cart:updated', {
        detail: { cart, source: 'cart-recommendation' },
      }));
      await this.removeUnqualifiedDiscoveryDiscount();
    } catch (error) {
      this.setRecommendationButtonState(button, 'default');
      this.setError(error.message || this.dataset.errorLabel);
    } finally {
      this.setLoading(false);
    }
  }

  async renderRecommendationAdd(html, button) {
    const recommendation = button?.closest('[data-cart-recommendation]');
    const productId = recommendation?.dataset.productId;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    window.AveyBottleExperiment?.prepareCart(parsed);
    const nextDrawer = parsed.querySelector('avey-cart-drawer');
    const nextContent = nextDrawer?.querySelector('[data-cart-drawer-content]');
    const currentContent = this.querySelector('[data-cart-drawer-content]');
    const currentScroll = currentContent?.querySelector('[data-cart-drawer-scroll]');
    const nextScroll = nextContent?.querySelector('[data-cart-drawer-scroll]');
    if (!productId || !nextDrawer || !nextContent || !currentContent || !currentScroll || !nextScroll) return false;

    const currentLinesContainer = currentScroll.querySelector('.avey-cart-drawer__lines');
    const nextLinesContainer = nextScroll.querySelector('.avey-cart-drawer__lines');
    const currentLines = [...(currentLinesContainer?.querySelectorAll(':scope > [data-cart-line]') || [])];
    const nextLines = [...(nextLinesContainer?.querySelectorAll(':scope > [data-cart-line]') || [])];
    if (!nextLinesContainer || nextLines.length !== currentLines.length + 1) return false;

    const matchedLines = this.matchCartLines(currentLines, nextLines);
    if (!matchedLines) return false;
    const { pairs: existingPairs, usedNextLines } = matchedLines;

    const addedLines = nextLines.filter((nextLine) => !usedNextLines.has(nextLine));
    if (addedLines.length !== 1 || addedLines[0].dataset.productId !== productId) return false;
    if (!(await this.prepareNewRecommendationCards(currentContent, nextContent))) return false;
    const addedSourceLine = addedLines[0];
    const addedLine = addedSourceLine.cloneNode(true);
    if (!this.moveRecommendationMedia(recommendation, addedLine)) return false;

    existingPairs.forEach(([currentLine, nextLine]) => this.syncCartLine(currentLine, nextLine));
    if (currentLinesContainer) {
      const addedIndex = nextLines.indexOf(addedSourceLine);
      if (addedIndex === 0) {
        currentLinesContainer.prepend(addedLine);
      } else {
        const previousNextLine = nextLines[addedIndex - 1];
        const previousPair = existingPairs.find(([, nextLine]) => nextLine === previousNextLine);
        if (!previousPair) return false;
        previousPair[0].after(addedLine);
      }
    } else {
      const newLinesContainer = nextLinesContainer.cloneNode(false);
      newLinesContainer.append(addedLine);
      currentScroll.querySelector('.avey-cart-drawer__empty')?.remove();
      currentScroll.prepend(newLinesContainer);
    }

    if (!this.syncRecommendationCards(currentContent, nextContent)) return false;
    return this.syncCartLayout(nextDrawer, currentContent, nextContent, currentScroll, nextScroll);
  }

  async renderLineRemoval(html, removedLine) {
    if (!(removedLine instanceof HTMLElement) || !removedLine.isConnected) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    window.AveyBottleExperiment?.prepareCart(parsed);
    const nextDrawer = parsed.querySelector('avey-cart-drawer');
    const nextContent = nextDrawer?.querySelector('[data-cart-drawer-content]');
    const currentContent = this.querySelector('[data-cart-drawer-content]');
    const currentScroll = currentContent?.querySelector('[data-cart-drawer-scroll]');
    const nextScroll = nextContent?.querySelector('[data-cart-drawer-scroll]');
    if (!nextDrawer || !nextContent || !currentContent || !currentScroll || !nextScroll) return false;

    const currentLinesContainer = currentScroll.querySelector('.avey-cart-drawer__lines');
    const currentLines = [...(currentLinesContainer?.querySelectorAll(':scope > [data-cart-line]') || [])];
    const nextLines = [...nextScroll.querySelectorAll('[data-cart-line]')];
    if (!currentLines.includes(removedLine) || nextLines.length !== currentLines.length - 1) return false;

    const retainedLines = currentLines.filter((currentLine) => currentLine !== removedLine);
    const matchedLines = this.matchCartLines(retainedLines, nextLines);
    if (!matchedLines || matchedLines.usedNextLines.size !== nextLines.length) return false;
    if (!(await this.prepareNewRecommendationCards(currentContent, nextContent))) return false;

    matchedLines.pairs.forEach(([currentLine, nextLine]) => this.syncCartLine(currentLine, nextLine));
    removedLine.remove();
    if (nextLines.length === 0) {
      currentLinesContainer?.remove();
      const nextEmpty = nextScroll.querySelector('.avey-cart-drawer__empty');
      if (!nextEmpty) return false;
      currentScroll.prepend(nextEmpty.cloneNode(true));
    }

    if (!this.syncRecommendationCards(currentContent, nextContent)) return false;
    return this.syncCartLayout(nextDrawer, currentContent, nextContent, currentScroll, nextScroll);
  }

  matchCartLines(currentLines, nextLines) {
    const usedNextLines = new Set();
    const pairs = currentLines.map((currentLine) => {
      const matches = nextLines.filter((nextLine) => (
        !usedNextLines.has(nextLine)
        && (
          nextLine.dataset.lineKey === currentLine.dataset.lineKey
          || (
            nextLine.dataset.productId === currentLine.dataset.productId
            && nextLine.dataset.currentVariantId === currentLine.dataset.currentVariantId
          )
        )
      ));
      if (matches.length !== 1 || !this.canKeepLineMedia(currentLine, matches[0])) return null;
      usedNextLines.add(matches[0]);
      return [currentLine, matches[0]];
    });
    return pairs.some((pair) => !pair) ? null : { pairs, usedNextLines };
  }

  moveRecommendationMedia(recommendation, addedLine) {
    const recommendationMedia = recommendation?.querySelector('.avey-cart-recommendation__media');
    const targetMedia = addedLine?.querySelector('.avey-cart-line__media');
    const image = recommendationMedia?.querySelector('img');
    if (!recommendationMedia || !targetMedia || !image?.complete || image.naturalWidth <= 0) return false;

    const decodedSource = image.currentSrc || image.src;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = decodedSource;
    image.loading = 'eager';
    image.decoding = 'sync';
    recommendationMedia.className = targetMedia.className;
    recommendationMedia.setAttribute('href', targetMedia.getAttribute('href') || '#');
    targetMedia.replaceWith(recommendationMedia);
    return true;
  }

  async prepareNewRecommendationCards(currentContent, nextContent) {
    const currentProductIds = new Set(
      [...currentContent.querySelectorAll('[data-cart-recommendation]')].map((card) => card.dataset.productId)
    );
    const newCards = [...nextContent.querySelectorAll('[data-cart-recommendation]')]
      .filter((card) => !currentProductIds.has(card.dataset.productId));
    for (const card of newCards) {
      if (!card.parentElement) return false;
      await this.prepareDetachedImages(card.parentElement);
    }
    return true;
  }

  syncRecommendationCards(currentContent, nextContent) {
    const currentSection = currentContent.querySelector('.avey-cart-recommendations');
    const nextSection = nextContent.querySelector('.avey-cart-recommendations');
    if (!nextSection) {
      currentSection?.remove();
      return true;
    }
    if (!currentSection) {
      const currentScroll = currentContent.querySelector('[data-cart-drawer-scroll]');
      if (!currentScroll) return false;
      currentScroll.append(nextSection.cloneNode(true));
      return true;
    }

    const currentList = currentSection.querySelector('.avey-cart-recommendations__list');
    const nextList = nextSection.querySelector('.avey-cart-recommendations__list');
    if (!currentList || !nextList) return false;
    const nextCards = [...nextList.querySelectorAll('[data-cart-recommendation]')];
    const nextProductIds = new Set(nextCards.map((card) => card.dataset.productId));

    currentList.querySelectorAll('[data-cart-recommendation]').forEach((currentCard) => {
      if (!nextProductIds.has(currentCard.dataset.productId)) currentCard.parentElement?.remove();
    });
    for (const nextCard of nextCards) {
      const currentCard = currentList.querySelector(
        `[data-cart-recommendation][data-product-id="${CSS.escape(nextCard.dataset.productId)}"]`
      );
      if (!currentCard) {
        const nextWrapper = nextCard.parentElement;
        if (!nextWrapper) return false;
        currentList.append(nextWrapper.cloneNode(true));
        continue;
      }
      this.syncAttributes(currentCard.parentElement, nextCard.parentElement);
      this.syncElement(currentCard, nextCard);
    }
    this.syncAttributes(currentSection, nextSection);
    const currentTitle = currentSection.querySelector('.avey-cart-recommendations__title');
    const nextTitle = nextSection.querySelector('.avey-cart-recommendations__title');
    if (currentTitle && nextTitle) {
      this.syncElement(currentTitle, nextTitle);
    } else if (currentTitle && !nextTitle) {
      currentTitle.remove();
    } else if (!currentTitle && nextTitle) {
      currentList.before(nextTitle.cloneNode(true));
    }
    return true;
  }

  syncCartLayout(nextDrawer, currentContent, nextContent, currentScroll, nextScroll) {
    const currentReward = currentContent.querySelector('[data-cart-reward]');
    const nextReward = nextContent.querySelector('[data-cart-reward]');
    if (currentReward && nextReward) {
      this.syncElement(currentReward, nextReward);
    } else if (!currentReward && nextReward) {
      currentContent.insertBefore(nextReward.cloneNode(true), currentScroll);
    } else if (currentReward && !nextReward) {
      currentReward.remove();
    }

    const currentFooter = currentContent.querySelector('.avey-cart-drawer__footer');
    const nextFooter = nextContent.querySelector('.avey-cart-drawer__footer');
    if (!nextFooter) return false;
    const footerParent = nextFooter.parentElement === nextScroll ? currentScroll : currentContent;
    const footerReference = footerParent === currentContent
      ? currentContent.querySelector('[data-cart-shade-picker]')
      : null;
    if (currentFooter) {
      this.syncElement(currentFooter, nextFooter);
      if (currentFooter.parentElement !== footerParent) {
        footerParent.insertBefore(currentFooter, footerReference);
      }
    } else {
      footerParent.insertBefore(nextFooter.cloneNode(true), footerReference);
    }

    this.syncAttributes(currentContent, nextContent);
    const currentTitle = this.querySelector('[data-cart-title]');
    const nextTitle = nextDrawer.querySelector('[data-cart-title]');
    if (currentTitle && nextTitle) this.syncElement(currentTitle, nextTitle);
    return true;
  }

  async prepareDetachedImages(root) {
    await Promise.all([...root.querySelectorAll('img[src]')].map(async (source) => {
      const image = new Image();
      if (source.sizes) image.sizes = source.sizes;
      if (source.srcset) image.srcset = source.srcset;
      image.src = source.src;
      try {
        await image.decode();
      } catch (_error) {
        return;
      }
      source.removeAttribute('srcset');
      source.removeAttribute('sizes');
      source.src = image.currentSrc || image.src;
      source.loading = 'eager';
      source.decoding = 'sync';
    }));
  }

  async refreshSection({ line = null, selectedVariantId = 0 } = {}) {
    if (!this.sectionId) return;
    // The drawer section is intentionally absent on the cart page. Render in
    // the current page context so shade changes also refresh the visible drawer.
    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`);
    if (!response.ok) throw new Error(this.dataset.errorLabel);
    const html = await response.text();
    if (!line || !this.renderStableSection(html, { line, selectedVariantId })) this.renderSection(html);
  }

  renderStableSection(html, { line, selectedVariantId = 0 }) {
    if (!(line instanceof HTMLElement) || !line.isConnected) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    window.AveyBottleExperiment?.prepareCart(parsed);
    const nextDrawer = parsed.querySelector('avey-cart-drawer');
    const nextContent = nextDrawer?.querySelector('[data-cart-drawer-content]');
    const currentContent = this.querySelector('[data-cart-drawer-content]');
    if (!nextDrawer || !nextContent || !currentContent) return false;

    const currentLines = [...currentContent.querySelectorAll('[data-cart-line]')];
    const nextLines = [...nextContent.querySelectorAll('[data-cart-line]')];
    if (currentLines.length !== nextLines.length) return false;

    const usedLines = new Set();
    const pairs = currentLines.map((currentLine) => {
      let candidates;
      if (currentLine === line && selectedVariantId) {
        candidates = nextLines.filter((nextLine) => (
          nextLine.dataset.productId === currentLine.dataset.productId
          && Number(nextLine.dataset.currentVariantId) === Number(selectedVariantId)
        ));
      } else {
        candidates = nextLines.filter((nextLine) => nextLine.dataset.lineKey === currentLine.dataset.lineKey);
        if (candidates.length !== 1) {
          candidates = nextLines.filter((nextLine) => (
            nextLine.dataset.productId === currentLine.dataset.productId
            && nextLine.dataset.currentVariantId === currentLine.dataset.currentVariantId
          ));
        }
      }
      const available = candidates.filter((candidate) => !usedLines.has(candidate));
      if (available.length !== 1) return null;
      usedLines.add(available[0]);
      return [currentLine, available[0]];
    });
    if (pairs.some((pair) => !pair)) return false;

    for (const [currentLine, nextLine] of pairs) {
      if (!this.canKeepLineMedia(currentLine, nextLine)) return false;
    }

    for (const [currentLine, nextLine] of pairs) {
      this.syncCartLine(currentLine, nextLine);
    }

    const nextTitle = nextDrawer.querySelector('[data-cart-title]');
    const currentTitle = this.querySelector('[data-cart-title]');
    if (currentTitle && nextTitle) this.syncElement(currentTitle, nextTitle);
    if (!this.syncOptionalBlock(currentContent, nextContent, '[data-cart-reward]')) return false;
    if (!this.syncOptionalBlock(currentContent, nextContent, '.avey-cart-recommendations')) return false;
    if (!this.syncOptionalBlock(currentContent, nextContent, '.avey-cart-drawer__footer')) return false;
    return true;
  }

  canKeepLineMedia(currentLine, nextLine) {
    const currentMedia = currentLine?.querySelector('.avey-cart-line__media');
    const nextMedia = nextLine?.querySelector('.avey-cart-line__media');
    const currentImage = currentMedia?.querySelector('img');
    const nextImage = nextMedia?.querySelector('img');
    return Boolean(
      currentMedia
      && nextMedia
      && this.getCanonicalMediaSource(currentImage) === this.getCanonicalMediaSource(nextImage)
    );
  }

  getCanonicalMediaSource(image) {
    if (!(image instanceof HTMLImageElement)) return '';
    const url = new URL(image.src, document.baseURI);
    url.searchParams.delete('width');
    return url.href;
  }

  syncCartLine(currentLine, nextLine) {
    this.syncAttributes(currentLine, nextLine);
    const currentMedia = currentLine.querySelector('.avey-cart-line__media');
    const nextMedia = nextLine.querySelector('.avey-cart-line__media');
    currentMedia.setAttribute('href', nextMedia.getAttribute('href') || '#');
    this.syncElement(
      currentLine.querySelector('.avey-cart-line__details'),
      nextLine.querySelector('.avey-cart-line__details')
    );
  }

  syncOptionalBlock(currentParent, nextParent, selector) {
    const currentBlock = currentParent.querySelector(selector);
    const nextBlock = nextParent.querySelector(selector);
    if (!currentBlock && !nextBlock) return true;
    if (!currentBlock || !nextBlock) return false;
    if (currentBlock.outerHTML !== nextBlock.outerHTML) this.syncElement(currentBlock, nextBlock);
    return true;
  }

  syncElement(currentElement, nextElement) {
    if (!(currentElement instanceof Element) || !(nextElement instanceof Element)) return;
    if (currentElement.matches('avey-cart-delivery')) return;
    this.syncAttributes(currentElement, nextElement);
    if (currentElement instanceof HTMLInputElement && currentElement.matches('[data-cart-quantity]')) {
      currentElement.value = nextElement.value;
    }
    if (currentElement instanceof HTMLTemplateElement && nextElement instanceof HTMLTemplateElement) {
      currentElement.content.replaceChildren(nextElement.content.cloneNode(true));
      return;
    }

    const currentNodes = [...currentElement.childNodes];
    const nextNodes = [...nextElement.childNodes];
    const length = Math.max(currentNodes.length, nextNodes.length);
    for (let index = 0; index < length; index += 1) {
      const currentNode = currentNodes[index];
      const nextNode = nextNodes[index];
      if (!currentNode && nextNode) {
        currentElement.append(nextNode.cloneNode(true));
      } else if (currentNode && !nextNode) {
        currentNode.remove();
      } else if (currentNode.nodeType === Node.TEXT_NODE && nextNode.nodeType === Node.TEXT_NODE) {
        if (currentNode.data !== nextNode.data) currentNode.data = nextNode.data;
      } else if (
        currentNode instanceof Element
        && nextNode instanceof Element
        && currentNode.tagName === nextNode.tagName
      ) {
        this.syncElement(currentNode, nextNode);
      } else {
        currentNode.replaceWith(nextNode.cloneNode(true));
      }
    }
  }

  syncAttributes(currentElement, nextElement) {
    [...currentElement.attributes].forEach((attribute) => {
      if (!nextElement.hasAttribute(attribute.name)) currentElement.removeAttribute(attribute.name);
    });
    [...nextElement.attributes].forEach((attribute) => {
      if (currentElement.getAttribute(attribute.name) !== attribute.value) {
        currentElement.setAttribute(attribute.name, attribute.value);
      }
    });
  }

  renderSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    window.AveyBottleExperiment?.prepareCart(parsed);
    const nextDrawer = parsed.querySelector('avey-cart-drawer');
    const nextTitle = nextDrawer?.querySelector('[data-cart-title]');
    const nextContent = nextDrawer?.querySelector('[data-cart-drawer-content]');
    const title = this.querySelector('[data-cart-title]');
    const content = this.querySelector('[data-cart-drawer-content]');
    if (!nextDrawer || !nextTitle || !nextContent || !title || !content) return;

    const itemCount = Number(nextDrawer.dataset.cartItemCount);
    if (Number.isFinite(itemCount)) this.updateCartCount(itemCount);

    const scrollPosition = content.querySelector('[data-cart-drawer-scroll]')?.scrollTop || 0;

    title.replaceChildren(...nextTitle.cloneNode(true).childNodes);
    content.replaceWith(nextContent.cloneNode(true));
    const nextScroll = this.querySelector('[data-cart-drawer-scroll]');
    if (nextScroll) nextScroll.scrollTop = scrollPosition;
  }

  updateCartCount(itemCount) {
    this.dataset.cartItemCount = String(itemCount);
    document.querySelectorAll('.avey-header__cart').forEach((cartLink) => {
      let count = cartLink.querySelector('.avey-header__cart-count');
      if (itemCount <= 0) {
        count?.remove();
        return;
      }

      if (!count) {
        count = document.createElement('span');
        count.className = 'avey-header__cart-count';
        count.setAttribute('aria-hidden', 'true');
        cartLink.append(count);
      }
      count.textContent = String(itemCount);
    });
  }

  normalizeQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
  }

  setLoading(loading) {
    this.dataset.loading = String(loading);
    this.querySelectorAll('.avey-cart-drawer__footer button[type="submit"]').forEach((button) => {
      button.disabled = loading;
    });
    this.querySelectorAll('[data-cart-page-variant]').forEach((select) => {
      select.disabled = loading;
      select.setAttribute('aria-busy', String(loading));
    });
    this.querySelector('avey-pro-cart-minimum')?.refresh?.();
    this.querySelector('[data-cart-drawer-content]')?.setAttribute('aria-busy', String(loading));
  }

  setLineQuantityLoading(line, loading, activeButton = null) {
    if (!(line instanceof HTMLElement)) return;
    const quantity = line.querySelector('.avey-cart-quantity');
    if (!quantity) return;
    quantity.classList.toggle('is-loading', loading);
    quantity.setAttribute('aria-busy', String(loading));
    quantity.querySelectorAll('[data-cart-quantity-change]').forEach((button) => {
      button.classList.toggle('is-active', loading && button === activeButton);
    });
    quantity.querySelectorAll('button, input').forEach((control) => {
      control.disabled = loading;
    });
  }

  setRemoveButtonLoading(button, loading) {
    if (!(button instanceof HTMLElement)) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', String(loading));
    button.setAttribute('aria-label', loading ? button.dataset.loadingLabel : button.dataset.defaultLabel);
    button.setAttribute('aria-disabled', String(loading));
    if (button instanceof HTMLButtonElement) button.disabled = loading;
  }

  setShadeOptionLoading(option, loading) {
    if (!(option instanceof HTMLButtonElement)) return;
    const grid = option.closest('.avey-cart-shade-picker__grid');
    grid?.querySelectorAll('button').forEach((button) => {
      button.disabled = loading || button.dataset.unavailable === 'true';
    });
    option.classList.toggle('is-loading', loading);
    option.setAttribute('aria-busy', String(loading));
  }

  get shadePicker() {
    return this.querySelector('[data-cart-shade-picker]');
  }

  setError(message) {
    const error = this.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  setRecommendationButtonState(button, state) {
    const label = button?.querySelector('[data-cart-recommendation-label]');
    if (!button || !label) return;
    const labels = {
      default: button.dataset.defaultLabel,
      loading: button.dataset.loadingLabel,
      success: button.dataset.successLabel,
    };
    button.dataset.addState = state;
    label.textContent = labels[state] || labels.default;
    button.setAttribute('aria-busy', String(state === 'loading'));
    button.disabled = state === 'loading';
  }
}

if (!customElements.get('avey-cart-drawer')) customElements.define('avey-cart-drawer', AveyCartDrawer);

class AveyCartDelivery extends HTMLElement {
  static contentPromise;

  connectedCallback() {
    this.hidden = false;
    this.drawer = this.closest('avey-cart-drawer');
    this.setAttribute('aria-busy', 'true');
    document.addEventListener('avey:overlay:open', this.onOverlayOpen);
    if (this.drawer?.querySelector('[data-avey-overlay]')?.open) this.load();
    else if (document.readyState === 'complete') this.schedulePreload();
    else window.addEventListener('load', this.schedulePreload, { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('avey:overlay:open', this.onOverlayOpen);
    this.cancelPreload();
  }

  schedulePreload = () => {
    if (!this.isConnected || this.loaded || this.loading) return;
    if (window.requestIdleCallback) {
      this.idleHandle = window.requestIdleCallback(() => this.load(), { timeout: 3000 });
    } else {
      this.preloadTimer = window.setTimeout(() => this.load(), 600);
    }
  };

  cancelPreload() {
    window.removeEventListener('load', this.schedulePreload);
    if (this.idleHandle !== undefined) window.cancelIdleCallback(this.idleHandle);
    if (this.preloadTimer !== undefined) window.clearTimeout(this.preloadTimer);
    this.idleHandle = undefined;
    this.preloadTimer = undefined;
  }

  onOverlayOpen = (event) => {
    if (event.detail?.overlay === this.drawer?.querySelector('[data-avey-overlay]')) this.load();
  };

  async load() {
    if (this.loaded || this.loading || !this.isConnected) return;
    this.cancelPreload();
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    delete this.dataset.loadFailed;
    try {
      AveyCartDelivery.contentPromise ||= fetch(this.dataset.sectionUrl, {
        headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(10000),
      }).then((response) => {
        if (!response.ok) throw new Error('Delivery content unavailable');
        return response.text();
      }).catch((error) => {
        AveyCartDelivery.contentPromise = null;
        throw error;
      });
      const html = await AveyCartDelivery.contentPromise;
      const section = new DOMParser().parseFromString(html, 'text/html');
      const projection = section.querySelector('[data-cart-delivery-projection]');
      const dialog = section.querySelector('[data-avey-overlay]');
      if (!projection?.querySelector('button') || !dialog) throw new Error('Delivery content missing');
      await Promise.all([
        this.loadStyle(this.dataset.deliveryStyle),
        this.loadStyle(this.dataset.destinationStyle),
        this.loadScript(this.dataset.projectionScript, 'avey-delivery-projection'),
        this.loadScript(this.dataset.destinationScript, 'avey-destination-search'),
      ]);
      if (!this.isConnected) return;
      if (!document.getElementById(dialog.id)) {
        dialog.classList.add('avey-cart-delivery-dialog');
        const title = dialog.querySelector('.avey-overlay__title');
        if (title) {
          dialog.querySelector('[data-avey-overlay-initial-focus]')?.removeAttribute('data-avey-overlay-initial-focus');
          title.tabIndex = -1;
          title.setAttribute('data-avey-overlay-initial-focus', '');
        }
        document.body.append(dialog);
      }
      this.replaceChildren(...projection.childNodes);
      this.loaded = true;
    } catch {
      AveyCartDelivery.contentPromise = null;
      this.dataset.loadFailed = 'true';
    } finally {
      this.loading = false;
      this.setAttribute('aria-busy', 'false');
    }
  }

  loadStyle(url) {
    const absolute = new URL(url, window.location.href).href;
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find((link) => link.href === absolute);
    if (existing) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = absolute;
      link.onload = resolve;
      link.onerror = () => { link.remove(); reject(new Error('Delivery styles unavailable')); };
      document.head.append(link);
    });
  }

  loadScript(url, elementName) {
    if (customElements.get(elementName)) return Promise.resolve();
    const absolute = new URL(url, window.location.href).href;
    if ([...document.scripts].some((script) => script.src === absolute)) return customElements.whenDefined(elementName);
    return import(absolute);
  }
}

if (!customElements.get('avey-cart-delivery')) customElements.define('avey-cart-delivery', AveyCartDelivery);
